import { z } from 'zod';
import { tableInputDefinitionSchema } from './model.types.js';
import { aliasMatchStateSchema } from './tool-results.types.js';
/**
 * Interaction request discriminated union for the table-interaction-surface feature.
 *
 * Declared in shared-types (Option B / ADR 0027) so the future agent↔web
 * `interaction_request` SSE protocol is additive — the Zod union can be
 * widened in-place when the deferred SSE variant ships.
 *
 * v1 IMPLEMENTS: `table-edit` only (editable unit-mix / rent-roll grid).
 *
 * v1 DECLARES (stub-rendered as `single-choice`): `single-choice` | `multi-choice`
 * | `free-text` | `numeric` | `confirm` — proves the shell generalises per the
 * fidelity-spec § "Generalization"; none are invoked by the production agent today.
 *
 * The shared `InteractionBase` fields (`id`, `prompt`) are inlined in each variant
 * arm (Zod discriminated-union pattern — no `z.merge` across arms; precedent: `realtime.ts`
 * `cellOpSchema`, `tool-results.types.ts` discriminated unions).
 *
 * NOTE: the production `table-edit` variant carries schema-native props:
 *   - `tableDef: TableInputDefinition` (the schema, from `model.types.ts`)
 *   - `conversationId: string`           (for the submit path via useTableInputsEdit)
 *   - `title?: string`                   (optional override display name)
 *   - `subtitle?: string`                (optional context line)
 * NOT the prototype's `columns[] + rows[]` mock shape (which is a harness convenience).
 * The re-homed `<TableGridEditor>` is fed its real props with zero translation layer.
 */
// ---------------------------------------------------------------------------
// table-edit variant (v1 IMPLEMENTED)
// ---------------------------------------------------------------------------
export const tableEditInteractionSchema = z.object({
    kind: z.literal('table-edit'),
    /** Unique interaction instance ID (caller-generated, used as React key). */
    id: z.string().min(1, { message: 'id is required' }),
    /** Prompt text rendered in the interaction surface header. */
    prompt: z.string().min(1, { message: 'prompt is required' }),
    /**
     * Schema-native table definition (from the model schema) — fed directly
     * to `<TableGridEditor>` as its `tableDef` prop without translation.
     */
    tableDef: tableInputDefinitionSchema,
    /**
     * Conversation ID threaded to `useTableInputsEdit().mutate()` on submit.
     * Matches the `conversationId` arg of `api.postTableEdit`.
     */
    conversationId: z.string().uuid({ message: 'conversationId must be a UUID' }),
    /** Optional display name override (defaults to `tableDef.displayName`). */
    title: z.string().optional(),
    /** Optional subtitle / context line rendered beneath the prompt. */
    subtitle: z.string().optional(),
});
// ---------------------------------------------------------------------------
// single-choice variant (v1 STUB-rendered to prove shell generalises)
// ---------------------------------------------------------------------------
export const singleChoiceInteractionSchema = z.object({
    kind: z.literal('single-choice'),
    id: z.string().min(1, { message: 'id is required' }),
    prompt: z.string().min(1, { message: 'prompt is required' }),
    options: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
        hint: z.string().optional(),
    }))
        .min(1, { message: 'single-choice requires at least one option' }),
});
// ---------------------------------------------------------------------------
// multi-choice variant (declared; not stub-rendered in v1)
// ---------------------------------------------------------------------------
export const multiChoiceInteractionSchema = z.object({
    kind: z.literal('multi-choice'),
    id: z.string().min(1, { message: 'id is required' }),
    prompt: z.string().min(1, { message: 'prompt is required' }),
    options: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
    }))
        .min(1, { message: 'multi-choice requires at least one option' }),
});
// ---------------------------------------------------------------------------
// free-text variant (declared; not rendered in v1)
// ---------------------------------------------------------------------------
export const freeTextInteractionSchema = z.object({
    kind: z.literal('free-text'),
    id: z.string().min(1, { message: 'id is required' }),
    prompt: z.string().min(1, { message: 'prompt is required' }),
    placeholder: z.string().optional(),
});
// ---------------------------------------------------------------------------
// numeric variant (declared; not rendered in v1)
// ---------------------------------------------------------------------------
export const numericInteractionSchema = z.object({
    kind: z.literal('numeric'),
    id: z.string().min(1, { message: 'id is required' }),
    prompt: z.string().min(1, { message: 'prompt is required' }),
    unit: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
});
// ---------------------------------------------------------------------------
// confirm variant (declared; not rendered in v1)
// ---------------------------------------------------------------------------
export const confirmInteractionSchema = z.object({
    kind: z.literal('confirm'),
    id: z.string().min(1, { message: 'id is required' }),
    prompt: z.string().min(1, { message: 'prompt is required' }),
    confirmLabel: z.string().optional(),
    cancelLabel: z.string().optional(),
});
// ---------------------------------------------------------------------------
// market-default-apply variant (market-default-apply-ux Phase 1 Session 1.3)
// ---------------------------------------------------------------------------
/**
 * Per-suggestion AI match candidate for an unmatched market field.
 * Shape mirrors the api-local LOCAL_DEFAULT_MATCH_SUGGESTIONS_SCHEMA item
 * (structural parity tested in apps/api default-match-suggestions.spec.ts).
 */
export const marketDefaultSuggestionItemSchema = z.object({
    namedRange: z.string(),
    displayName: z.string(),
    reason: z.string(),
});
/**
 * The 7th `interactionRequest` arm — emitted by the agent for unmatched /
 * informational market fields so the web layer can prompt the user to select
 * or override the suggested input target.
 *
 * Fields:
 *   kind              — unique discriminant 'market_default_apply'
 *   id                — unique interaction instance ID (caller-generated)
 *   prompt            — prompt text rendered in the interaction surface header
 *   conversationId    — threaded to the submit path (mirrors table-edit arm)
 *   marketField       — the market data field this interaction operates on
 *   value             — the market data value (numeric)
 *   unit              — optional unit string (e.g. '$', '%')
 *   aliasMatchState   — 'intentionally_unmapped' | 'genuinely_unmatched'
 *                       (ADR 0003 — informational is the primary entry point)
 *   suggestions       — top-N AI match candidates; defaults [] (fail-open)
 *   options           — eligible input dropdown options (min 1 required)
 *   explainPrompt     — optional label for the free-text explain field;
 *                       the free-text VALUE is captured at submit and threaded
 *                       via the model-edit `note` field (NOT a schema field here)
 *
 * Authoring precedent: tableEditInteractionSchema (id/prompt/conversationId) +
 * singleChoiceInteractionSchema (options inline-object .min(1)).
 */
export const marketDefaultApplyInteractionSchema = z.object({
    kind: z.literal('market_default_apply'),
    id: z.string().min(1, { message: 'id is required' }),
    prompt: z.string().min(1, { message: 'prompt is required' }),
    conversationId: z.string().uuid({ message: 'conversationId must be a UUID' }),
    marketField: z.string(),
    value: z.number(),
    unit: z.string().optional(),
    aliasMatchState: aliasMatchStateSchema,
    suggestions: z.array(marketDefaultSuggestionItemSchema).default([]),
    options: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
        hint: z.string().optional(),
    }))
        .min(1, {
        message: 'market_default_apply requires at least one eligible input option',
    }),
    explainPrompt: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Top-level discriminated union
// ---------------------------------------------------------------------------
/**
 * Discriminated union on `kind`. v1 implements `table-edit`; the remaining
 * five variants are declared for future agent↔web SSE protocol extension.
 * v2 adds `market_default_apply` (market-default-apply-ux Phase 1 Session 1.3).
 *
 * Precedent: `realtime.ts:cellOpSchema`, `tool-results.types.ts` unions.
 */
export const interactionRequestSchema = z.discriminatedUnion('kind', [
    tableEditInteractionSchema,
    singleChoiceInteractionSchema,
    multiChoiceInteractionSchema,
    freeTextInteractionSchema,
    numericInteractionSchema,
    confirmInteractionSchema,
    marketDefaultApplyInteractionSchema,
]);
